import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { Outlet } from 'react-router-dom';

// Piezas de página compartidas: encabezado, secciones con tabla, franja de indicadores y píldoras de estado.
// Las tablas viven dentro de <Section>, pegadas a los bordes, y el tema les da el resto (ver theme.ts).

/** Contenedor de página: margen propio y ancho máximo (el layout no pone padding). */
export const PageShell: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 2, md: 3 }, pb: 6, maxWidth: 1480, mx: 'auto' }}>
    {children ?? <Outlet />}
  </Box>
);

export const PageHeader: React.FC<{ title: React.ReactNode; description?: React.ReactNode; actions?: React.ReactNode }> = ({ title, description, actions }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }} sx={{ mb: 3 }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography component="h1" sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.15 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: '72ch', lineHeight: 1.55 }}>
          {description}
        </Typography>
      )}
    </Box>
    {actions && <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>{actions}</Stack>}
  </Stack>
);

/** Bloque con título y acciones; una tabla adentro queda a ras de los bordes. */
export const Section: React.FC<{
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  padded?: boolean;
  sx?: object;
}> = ({ title, description, actions, children, padded, sx }) => (
  <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 3, ...sx }}>
    {(title || actions) && (
      <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {title && <Typography sx={{ fontWeight: 650, fontSize: 15 }}>{title}</Typography>}
          {description && <Typography variant="caption" color="text.secondary">{description}</Typography>}
        </Box>
        {actions}
      </Stack>
    )}
    {padded ? <Box sx={{ p: 2.5 }}>{children}</Box> : children}
  </Paper>
);

/** Franja de indicadores: un solo bloque dividido, no tarjetas sueltas. */
export const KpiStrip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paper
    variant="outlined"
    sx={{
      display: 'grid', mb: 3,
      gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${React.Children.toArray(children).filter(Boolean).length}, minmax(0, 1fr))` },
      '& > *': { borderLeft: '1px solid', borderTop: '1px solid', borderColor: 'divider', ml: '-1px', mt: '-1px' },
    }}
  >
    {children}
  </Paper>
);

export type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'neutral';

export const TONES: Record<Tone, { fg: string; bg: string; dot: string }> = {
  ok: { fg: '#0F6B45', bg: '#E8F5EE', dot: '#16A36A' },
  warn: { fg: '#8A5200', bg: '#FFF4DE', dot: '#E59A0C' },
  bad: { fg: '#A8251B', bg: '#FDECEA', dot: '#E0402F' },
  info: { fg: '#2B3BB8', bg: '#ECEEFE', dot: '#384DF4' },
  neutral: { fg: '#4A5361', bg: '#F0F0EE', dot: '#9AA1AC' },
};

/** Valor de un indicador. El tono solo pinta el número cuando hay algo que mirar. */
export const KpiItem: React.FC<{ label: React.ReactNode; value: React.ReactNode; hint?: React.ReactNode; tone?: Tone }> = ({ label, value, hint, tone }) => (
  <Box sx={{ px: 2.5, py: 2, minWidth: 0 }}>
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>{label}</Typography>
    <Typography sx={{
      fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25, mt: 0.5,
      fontVariantNumeric: 'tabular-nums', color: tone && tone !== 'neutral' ? TONES[tone].fg : 'text.primary',
    }}>
      {value}
    </Typography>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Box>
);

/** Píldora de estado con punto de color, tenue (no chip sólido). */
export const StatusPill: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => {
  const t = TONES[tone];
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: '2px', borderRadius: 999,
      bgcolor: t.bg, color: t.fg, fontSize: 12.5, fontWeight: 600, lineHeight: 1.6, whiteSpace: 'nowrap',
    }}>
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: t.dot, flexShrink: 0 }} />
      {children}
    </Box>
  );
};

/** Fila vacía de tabla que dice qué hacer. */
export const EmptyState: React.FC<{ title: React.ReactNode; hint?: React.ReactNode; action?: React.ReactNode }> = ({ title, hint, action }) => (
  <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
    <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
    {hint && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '56ch', mx: 'auto' }}>{hint}</Typography>}
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Box>
);
